import type { KpiPolarity } from "@nexus/contracts"
import { computePctChange } from "@/features/dashboard/lib/format"
import { getKpiTone, type KpiTone } from "@/features/overview/lib/topicDetailKpis"

/** Must match `ACCOUNT_KPI_KEY_PREFIX` in drilldown-my-brand.sql.ts. */
export const ACCOUNT_KPI_KEY_PREFIX = "account__"

export function accountKpiKey(name: string): string {
  return `${ACCOUNT_KPI_KEY_PREFIX}${name}`
}

export type CardTrendTone = KpiTone
export type CardTrendDirection = "up" | "down" | null

export interface CardKpiTrend {
  trendLabel: string
  trendTone: CardTrendTone
  trendDirection: CardTrendDirection
}

/** Drawer/Figma parity: offsets at 1 decimal; magnitudes of 1,000+ abbreviate. */
const OFFSET_DECIMALS = 1
const PILL_ABBREVIATE_THRESHOLD = 1_000
const PILL_UNITS = [
  { limit: 1_000_000_000, suffix: "b" },
  { limit: 1_000_000, suffix: "m" },
  { limit: 1_000, suffix: "k" },
] as const

const FREQUENCY_KPI_NAME = "Frequency"

function roundTo(value: number, decimals: number): number {
  const p = Math.pow(10, decimals)
  return Math.round(value * p) / p
}

function formatPillPercent(value: number): string {
  const abs = Math.abs(value)
  if (abs < PILL_ABBREVIATE_THRESHOLD) return `${value}`

  const sign = value < 0 ? "-" : ""
  const unit = PILL_UNITS.find(({ limit }) => abs >= limit) ?? PILL_UNITS.at(-1)!
  return `${sign}${roundTo(abs / unit.limit, 1)}${unit.suffix}`
}

export function shouldHideCardTrend(options: { kpiId: string; hasSumFormula?: boolean }): boolean {
  return options.hasSumFormula === true || options.kpiId === FREQUENCY_KPI_NAME
}

/**
 * `(value - accountAvg) / accountAvg` vs the filtered account average.
 * Colour follows catalog polarity (POSITIVE = low-is-good). Hide when the
 * baseline is missing/zero — never invent a 0% from a divide-by-zero.
 */
export function resolveCardKpiTrend(
  kpiId: string,
  value: number | null,
  accountAvg: number | null,
  options: { polarity?: KpiPolarity; hasSumFormula?: boolean } = {},
): CardKpiTrend | null {
  if (shouldHideCardTrend({ kpiId, hasSumFormula: options.hasSumFormula })) return null
  if (value === null || accountAvg === null || accountAvg === 0) return null

  const pct = computePctChange(value, accountAvg)
  if (Number.isNaN(pct)) return null

  const rounded = roundTo(pct, OFFSET_DECIMALS)
  const trendTone = getKpiTone(kpiId, rounded, options.polarity)
  const trendDirection: CardTrendDirection = rounded > 0 ? "up" : rounded < 0 ? "down" : null
  const trendLabel = `${rounded > 0 ? "+" : ""}${formatPillPercent(rounded)}%`
  return { trendLabel, trendTone, trendDirection }
}

/** Primary pill shows magnitude only; the arrow carries the sign. */
export function unsignedTrendLabel(trendLabel: string): string {
  return trendLabel.replace(/^[+-]/, "")
}
